import type { MessageTypeWithStats, TimelineAlert } from '@owl/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type BucketSize,
  floorToBucket,
  generateBucketSlots,
  pickBucketSize,
} from '../../lib/buckets';

type TimelineBucket = { bucket: string; counts: Record<string, number> };

// Neutral types are hidden from the bars by default so SUCCESS/WARN/ERROR/FATAL
// dominate the visual. The legend still shows them with their totals and the user
// can click any chip to toggle it in/out of the bars. The choice persists in
// localStorage by NAME (not id) so it survives BD reseeds and id rotations.
const DEFAULT_HIDDEN_TYPE_NAMES = ['INFO', 'DEBUG', 'TRACE', 'HEALTH'] as const;
const HIDDEN_TYPES_STORAGE_KEY = 'owl.timeline.hiddenTypes';

// Triggered alerts render in a lane above the bars, one segment per bar that had any.
// Grouping per bar is the point: several alerts inside one interval used to stack as
// overlapping 8px pins that could not be told apart, let alone clicked. The lane sits
// outside the plot, so severity colours cannot be mistaken for message-type bars.
const SEVERITY_RANK = { low: 0, medium: 1, high: 2, critical: 3 } as const;
const SEVERITY_COLOR: Record<TimelineAlert['severity'], string> = {
  low: '#6b7280',
  medium: '#fbbf24',
  high: '#f97316',
  critical: '#dc2626',
};
const SEVERITY_LABEL: Record<TimelineAlert['severity'], string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
};
// A card that closes the instant the pointer leaves the lane cannot be reached to click
// an item inside it; this is the grace period for crossing that gap.
const ALERT_CARD_CLOSE_DELAY_MS = 180;
const alertTimeFmt = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function loadHiddenTypeNames(): Set<string> | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(HIDDEN_TYPES_STORAGE_KEY);
  if (raw === null) return null;
  return new Set(raw.split(',').filter(Boolean));
}

function saveHiddenTypeNames(names: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HIDDEN_TYPES_STORAGE_KEY, [...names].join(','));
  } catch {
    // ignore quota / private-mode errors
  }
}

type Props = {
  timeline: TimelineBucket[];
  messageTypes: MessageTypeWithStats[];
  alertEvents?: TimelineAlert[];
  from: string;
  to: string;
  height?: number;
  showLegend?: boolean;
  showAlerts?: boolean;
  // Optional parent notification so other widgets (e.g. distribution donut) can mirror the filter.
  onHiddenTypesChange?: (names: Set<string>) => void;
  // Fired when the user clicks a bar. `messageTypeIds` are the types currently drawn in the
  // bars (so the log count the caller shows matches the number printed on the bar), and
  // `messageTypeId` is set when the click landed on one specific stacked segment.
  onBucketSelect?: (sel: TimelineSelection) => void;
  // Fired when the user picks one alert. Only alerts whose triggering log still exists
  // are selectable: there is nothing to open for one that retention has purged.
  onAlertSelect?: (alert: TimelineAlert) => void;
};

export type TimelineSelection = {
  fromMs: number;
  size: BucketSize;
  messageTypeIds: string[];
  messageTypeId?: string;
};

export const HIDDEN_TIMELINE_TYPES_STORAGE_KEY = HIDDEN_TYPES_STORAGE_KEY;

export function readHiddenTimelineTypes(): Set<string> {
  return loadHiddenTypeNames() ?? new Set<string>(DEFAULT_HIDDEN_TYPE_NAMES);
}

function useIsNarrow(maxWidth: number): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= maxWidth,
  );
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth <= maxWidth);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [maxWidth]);
  return narrow;
}

function formatSlotLabel(t: number, size: BucketSize): string {
  if (size === '5min' || size === '30min') {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(t));
  }
  const parts = new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    day: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(t));
  const key = size === 'hour' ? 'hour' : 'day';
  return parts.find((p) => p.type === key)?.value ?? '';
}

export function StackedTimeline({
  timeline,
  messageTypes,
  alertEvents,
  from,
  to,
  height = 220,
  showLegend = true,
  showAlerts = true,
  onHiddenTypesChange,
  onBucketSelect,
  onAlertSelect,
}: Props) {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();

  const visibleAlerts = useMemo(
    () =>
      (alertEvents ?? []).filter((e) => {
        const t = new Date(e.triggeredAt).getTime();
        return t >= fromMs && t <= toMs;
      }),
    [alertEvents, fromMs, toMs],
  );

  const [hoverSlot, setHoverSlot] = useState<number | null>(null);
  const size = pickBucketSize(fromMs, toMs);
  const slots = generateBucketSlots(fromMs, toMs, size);

  // Alerts keyed by the slot they fall in, newest first within each slot.
  const alertsBySlot = useMemo(() => {
    const bySlot = new Map<number, TimelineAlert[]>();
    for (const alert of visibleAlerts) {
      const key = floorToBucket(new Date(alert.triggeredAt), size).getTime();
      const group = bySlot.get(key);
      if (group) group.push(alert);
      else bySlot.set(key, [alert]);
    }
    for (const group of bySlot.values()) {
      group.sort((a, b) => Date.parse(b.triggeredAt) - Date.parse(a.triggeredAt));
    }
    return bySlot;
  }, [visibleAlerts, size]);

  const [openAlertSlot, setOpenAlertSlot] = useState<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const cancelAlertCardClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openAlertCard = (slot: number) => {
    cancelAlertCardClose();
    setOpenAlertSlot(slot);
  };
  const scheduleAlertCardClose = () => {
    cancelAlertCardClose();
    closeTimerRef.current = window.setTimeout(
      () => setOpenAlertSlot(null),
      ALERT_CARD_CLOSE_DELAY_MS,
    );
  };
  // A pending close must not fire into an unmounted chart.
  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const isNarrow = useIsNarrow(640);
  const labelStep = isNarrow
    ? size === 'day'
      ? 5
      : size === 'hour'
        ? 6
        : 4
    : size === '5min' || size === '30min'
      ? 2
      : 1;

  // Hidden message types live by NAME (stable across BD reseeds / id rotations)
  // and persist in localStorage. First visit (key absent) applies the defaults.
  const [hiddenTypeNames, setHiddenTypeNames] = useState<Set<string>>(
    () => loadHiddenTypeNames() ?? new Set<string>(DEFAULT_HIDDEN_TYPE_NAMES),
  );
  // Notify the parent once on mount (and whenever the set changes) so widgets like the
  // distribution donut can stay in sync without duplicating the localStorage read.
  useEffect(() => {
    onHiddenTypesChange?.(hiddenTypeNames);
  }, [hiddenTypeNames, onHiddenTypesChange]);
  const hiddenTypeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const mt of messageTypes) {
      if (hiddenTypeNames.has(mt.name)) ids.add(mt.id);
    }
    return ids;
  }, [messageTypes, hiddenTypeNames]);
  const toggleType = (id: string) => {
    const mt = messageTypes.find((m) => m.id === id);
    if (!mt) return;
    setHiddenTypeNames((prev) => {
      const next = new Set(prev);
      if (next.has(mt.name)) next.delete(mt.name);
      else next.add(mt.name);
      saveHiddenTypeNames(next);
      return next;
    });
  };

  // Index the API timeline by its floored epoch-ms key. We re-floor here so
  // any tz quirk from the driver (timestamp without tz vs with tz) collapses
  // to the same slot the frontend generated.
  const bucketByTime = new Map<number, Record<string, number>>();
  for (const b of timeline) {
    const key = floorToBucket(new Date(b.bucket), size).getTime();
    bucketByTime.set(key, b.counts);
  }

  // Per-slot totals consider only types visible in the bars. Hidden types still
  // appear in the legend with their full counts, but they don't drive the scale.
  const totalsPerSlot = slots.map((t) => {
    const counts = bucketByTime.get(t);
    if (!counts) return 0;
    let sum = 0;
    for (const [id, v] of Object.entries(counts)) {
      if (hiddenTypeIds.has(id)) continue;
      sum += v;
    }
    return sum;
  });
  const max = Math.max(...totalsPerSlot, 4);

  const W = 100;
  const H = 100;
  const slotCount = slots.length;
  const barW = Math.max(0.1, W / slotCount - 0.5);

  // Types currently drawn in the bars: what a bucket click should filter by, so the
  // modal it opens shows exactly the logs the bar counted.
  const visibleTypeIds = messageTypes.filter((mt) => !hiddenTypeIds.has(mt.id)).map((mt) => mt.id);
  const selectable = Boolean(onBucketSelect);
  const selectSlot = (t: number, messageTypeId?: string) =>
    onBucketSelect?.({ fromMs: t, size, messageTypeIds: visibleTypeIds, messageTypeId });

  // Which stacked segment sits under the click, given how far up the bar the pointer
  // landed (0 = baseline, 1 = top of the plot area). Returns undefined when the click
  // was above the bar, which selects the whole bucket instead of one type.
  function segmentAt(t: number, yFromBottom: number): string | undefined {
    const counts = bucketByTime.get(t);
    if (!counts) return undefined;
    const value = (yFromBottom / 0.92) * max;
    if (value < 0) return undefined;
    let acc = 0;
    for (const mt of messageTypes) {
      if (hiddenTypeIds.has(mt.id)) continue;
      const v = counts[mt.id] || 0;
      if (!v) continue;
      acc += v;
      if (value <= acc) return mt.id;
    }
    return undefined;
  }

  const openGroup = openAlertSlot === null ? undefined : alertsBySlot.get(openAlertSlot);
  const openSlotIndex = openAlertSlot === null ? -1 : slots.indexOf(openAlertSlot);
  const openCenterPct = openSlotIndex < 0 ? 50 : ((openSlotIndex + 0.5) / slots.length) * 100;
  // Keep the card inside the chart near either edge instead of centring it off-canvas.
  const cardAlign = openCenterPct < 18 ? 'start' : openCenterPct > 82 ? 'end' : 'center';

  return (
    <div className="chart-wrap">
      {showAlerts && alertsBySlot.size > 0 && (
        <div className="tl-alert-lane">
          {slots.map((t) => {
            const group = alertsBySlot.get(t);
            if (!group) return <span key={`lane-${t}`} className="tl-alert-cell" />;
            const worst = group.reduce((a, b) =>
              SEVERITY_RANK[b.severity] > SEVERITY_RANK[a.severity] ? b : a,
            );
            // A bar whose alerts were all silenced by cooldown still happened, but it is
            // drawn quieter than one that actually notified someone.
            const allSilenced = group.every((a) => a.silenced);
            const single = group.length === 1 ? group[0] : undefined;
            const segClass = [
              'tl-alert-seg',
              allSilenced ? 'silenced' : '',
              openAlertSlot === t ? 'open' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <span key={`lane-${t}`} className="tl-alert-cell">
                <button
                  type="button"
                  className={segClass}
                  style={{ background: SEVERITY_COLOR[worst.severity] }}
                  aria-label={`${group.length} alerta${group.length === 1 ? '' : 's'} · ${formatSlotLabel(t, size)}`}
                  onMouseEnter={() => openAlertCard(t)}
                  onMouseLeave={scheduleAlertCardClose}
                  onFocus={() => openAlertCard(t)}
                  onBlur={scheduleAlertCardClose}
                  onClick={() => {
                    // One alert: go straight to it. Several: the card is the chooser.
                    if (single?.log && single.triggeringLogId && onAlertSelect) {
                      onAlertSelect(single);
                    } else {
                      openAlertCard(t);
                    }
                  }}
                >
                  {group.length > 1 ? group.length : null}
                </button>
              </span>
            );
          })}
        </div>
      )}
      {showAlerts && openGroup && (
        <div
          className={`tl-alert-card align-${cardAlign}`}
          style={{ left: `${openCenterPct}%` }}
          onMouseEnter={cancelAlertCardClose}
          onMouseLeave={scheduleAlertCardClose}
        >
          <div className="tl-alert-card-h">
            {openGroup.length} alerta{openGroup.length === 1 ? '' : 's'}
          </div>
          <div className="tl-alert-card-list">
            {openGroup.map((alert) => {
              const selectable = Boolean(alert.log && alert.triggeringLogId && onAlertSelect);
              return (
                <button
                  type="button"
                  key={alert.id}
                  className="tl-alert-item"
                  disabled={!selectable}
                  onClick={() => {
                    if (!selectable) return;
                    setOpenAlertSlot(null);
                    onAlertSelect?.(alert);
                  }}
                >
                  <span
                    className="tl-alert-dot"
                    style={{ background: SEVERITY_COLOR[alert.severity] }}
                  />
                  <span className="tl-alert-body">
                    <span className="tl-alert-top">
                      <strong>{alert.applicationName}</strong>
                      <span className="tl-alert-time">
                        {alertTimeFmt.format(new Date(alert.triggeredAt))}
                      </span>
                    </span>
                    <span className="tl-alert-rule">
                      {alert.ruleName} · {SEVERITY_LABEL[alert.severity]}
                      {alert.silenced ? ' · silenciada' : ''}
                    </span>
                    <span className="tl-alert-msg">
                      {alert.log
                        ? `${alert.log.messageTypeName ?? ''} ${alert.log.message}`.trim()
                        : 'El log ya no existe (purgado por retención)'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: '100%', height, display: 'block' }}
        >
          <title>Logs per time slot, stacked by message type</title>
          {[0.25, 0.5, 0.75, 1].map((g) => (
            <line
              key={g}
              x1="0"
              y1={H - g * H * 0.92}
              x2={W}
              y2={H - g * H * 0.92}
              stroke="rgba(255,255,255,0.04)"
              strokeWidth="0.2"
            />
          ))}
          {slots.map((_, i) => {
            if (i === 0) return null;
            const x = i * (W / slotCount);
            const major =
              size === '5min'
                ? i % 6 === 0
                : size === '30min'
                  ? i % 2 === 0
                  : size === 'hour'
                    ? i % 6 === 0
                    : i % 7 === 0;
            return (
              <line
                key={`v-${x}`}
                x1={x}
                y1={H - H * 0.92}
                x2={x}
                y2={H}
                stroke={major ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.05)'}
                strokeWidth={major ? '0.25' : '0.15'}
              />
            );
          })}
          {slots.map((t, i) => {
            const counts = bucketByTime.get(t);
            if (!counts) return null;
            const x = i * (W / slotCount) + 0.25;
            let acc = 0;
            const segs: React.ReactNode[] = [];
            for (const mt of messageTypes) {
              if (hiddenTypeIds.has(mt.id)) continue;
              const v = counts[mt.id] || 0;
              if (!v) continue;
              const h = (v / max) * H * 0.92;
              segs.push(
                <rect
                  key={mt.id}
                  x={x}
                  y={H - acc - h}
                  width={barW}
                  height={h}
                  fill={mt.color}
                  opacity={hoverSlot !== null && hoverSlot !== t ? 0.55 : 0.92}
                  rx="0.3"
                />,
              );
              acc += h;
            }
            return <g key={t}>{segs}</g>;
          })}
          {selectable &&
            slots.map((t, i) => (
              // Full-height transparent hit area: the stacked segments are only a
              // couple of pixels tall on quiet buckets and would be unclickable.
              <rect
                key={`hit-${t}`}
                x={i * (W / slotCount)}
                y={0}
                width={W / slotCount}
                height={H}
                fill={hoverSlot === t ? 'rgba(255,255,255,0.05)' : 'transparent'}
                style={{ cursor: 'pointer' }}
                // biome-ignore lint/a11y/useSemanticElements: <button> is not valid inside <svg>; role plus tabIndex and onKeyDown is the SVG equivalent.
                role="button"
                tabIndex={0}
                aria-label={`Ver logs de ${formatSlotLabel(t, size)}`}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  selectSlot(t);
                }}
                onMouseEnter={() => setHoverSlot(t)}
                onMouseLeave={() => setHoverSlot((prev) => (prev === t ? null : prev))}
                onClick={(e) => {
                  const box = e.currentTarget.getBoundingClientRect();
                  const yFromBottom = box.height > 0 ? (box.bottom - e.clientY) / box.height : 0;
                  selectSlot(t, segmentAt(t, yFromBottom));
                }}
              >
                <title>{`${formatSlotLabel(t, size)} · ${totalsPerSlot[i] ?? 0} logs`}</title>
              </rect>
            ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {slots.map((t, i) => {
            const total = totalsPerSlot[i] ?? 0;
            if (total === 0) return null;
            const left = ((i + 0.5) / slotCount) * 100;
            const top = (1 - (total / max) * 0.92) * 100;
            const barPx = (total / max) * 0.92 * height;
            const inside = barPx >= 14;
            return (
              <span
                key={`n-${t}`}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  top: `${top}%`,
                  transform: inside ? 'translate(-50%, 3px)' : 'translate(-50%, calc(-100% - 2px))',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  fontWeight: 500,
                  color: inside ? 'rgba(255,255,255,0.95)' : 'var(--fg-2)',
                  textShadow: inside ? '0 1px 2px rgba(0,0,0,0.55)' : undefined,
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.02em',
                }}
              >
                {total}
              </span>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', marginTop: 6 }}>
        {slots.map((t, i) => (
          <span
            key={`x-${t}`}
            style={{
              flex: 1,
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--fg-4)',
              letterSpacing: '0.04em',
            }}
          >
            {i % labelStep === 0 ? formatSlotLabel(t, size) : ''}
          </span>
        ))}
      </div>
      {showLegend && (
        <div className="chart-legend" style={{ marginTop: 12 }}>
          {messageTypes.map((mt) => {
            const total = timeline.reduce((a, b) => a + (b.counts[mt.id] || 0), 0);
            if (total === 0) return null;
            const isHidden = hiddenTypeIds.has(mt.id);
            return (
              <button
                type="button"
                key={mt.id}
                className={`legend-item legend-item-toggle${isHidden ? ' legend-item-off' : ''}`}
                onClick={() => toggleType(mt.id)}
                title={isHidden ? `Show ${mt.name} in chart` : `Hide ${mt.name} from chart`}
              >
                <span className="legend-sq" style={{ background: mt.color }} />
                <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.04em' }}>
                  {mt.name}
                </span>
                <span className="mono" style={{ color: 'var(--fg-4)' }}>
                  {total}
                </span>
              </button>
            );
          })}
          {showAlerts && visibleAlerts.length > 0 && (
            <span className="legend-item" title="La franja superior, coloreada por severidad">
              <span className="legend-sq" style={{ background: SEVERITY_COLOR.high }} />
              <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.04em' }}>
                ALERTAS
              </span>
              <span className="mono" style={{ color: 'var(--fg-4)' }}>
                {visibleAlerts.length}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
